
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { Separator } from "@/components/ui/separator";
import SEO from "@/components/SEO";
import { cn } from "@/lib/utils";
import { seoKnowledgeBaseData } from "@/data/seoKnowledgeBase";
import { SEOKnowledgeItem } from "@/types";
import { generateBreadcrumbSchema } from "@/utils/seoUtils";

const KnowledgeBase = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<{
    [key: string]: { label: string; articles: SEOKnowledgeItem[] };
  }>({
    all: { label: "All Topics", articles: [] },
    basics: { label: "SEO Basics", articles: [] },
    technical: { label: "Technical SEO", articles: [] },
    content: { label: "Content SEO", articles: [] },
    local: { label: "Local SEO", articles: [] },
    link: { label: "Link Building", articles: [] },
    industry: { label: "Industry-Specific", articles: [] },
    seo_strategy: { label: "SEO Strategy", articles: [] },
    seo_analytics: { label: "SEO Analytics", articles: [] },
  });
  
  // SEO metadata for the knowledge base page
  const seoData = {
    title: "SEO Knowledge Base | Los Angeles SEO Group",
    description: "Explore our comprehensive SEO knowledge base covering basics to advanced strategies for improving your website's search engine rankings.",
    canonical: `https://losangelesseogroup.com${location.pathname}`,
    keywords: "SEO knowledge base, SEO tutorials, search engine optimization resources, SEO guide, SEO tips, technical SEO, content SEO, local SEO",
    jsonLd: generateBreadcrumbSchema([
      { name: "Home", url: "https://losangelesseogroup.com/" },
      { name: "Knowledge Base", url: "https://losangelesseogroup.com/knowledge-base" }
    ])
  };
  
  useEffect(() => {
    try {
      // Simulate data loading
      setIsLoading(true);
      setError(null);
      
      console.log("KnowledgeBase: Initial render");
      console.log("KnowledgeBase: Available articles:", seoKnowledgeBaseData?.length || 0);
      
      // Check if data is available
      if (!Array.isArray(seoKnowledgeBaseData) || seoKnowledgeBaseData.length === 0) {
        console.error("KnowledgeBase: No data available or invalid data format");
        setError("No knowledge base articles available. Please try again later.");
        setIsLoading(false);
        return;
      }
      
      // Initialize and populate categories
      populateCategories();
      
      // Set loading to false when done
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading knowledge base data:", error);
      setError("An error occurred while loading the knowledge base. Please try again later.");
      setIsLoading(false);
    }
  }, []);
  
  const populateCategories = () => {
    try {
      // Make sure seoKnowledgeBaseData exists and is an array before sorting
      if (!Array.isArray(seoKnowledgeBaseData)) {
        console.error("seoKnowledgeBaseData is not an array:", seoKnowledgeBaseData);
        setError("Invalid data format. Please try again later.");
        return;
      }
      
      // Create a new categories object to avoid state mutation
      const newCategories = { ...categories };
      
      // Reset category articles
      Object.keys(newCategories).forEach(key => {
        newCategories[key].articles = [];
      });
      
      // Sort articles by date (newest first) and organize by category
      const sortedArticles = [...seoKnowledgeBaseData].sort((a, b) => {
        // Handle potential undefined dates
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log("KnowledgeBase: Sorted articles:", sortedArticles.length);
      
      // Populate the categories map
      sortedArticles.forEach(article => {
        if (!article) {
          console.error("KnowledgeBase: Undefined article found");
          return;
        }
        
        // All articles go in the "all" category
        newCategories.all.articles.push(article);
        
        // Add to specific category if it exists in our map
        if (article.category && newCategories[article.category]) {
          newCategories[article.category].articles.push(article);
        } else {
          // If category doesn't exist in our map, add to industry-specific
          if (newCategories.industry) {
            newCategories.industry.articles.push(article);
          }
        }
      });
      
      // Log category counts for debugging
      Object.entries(newCategories).forEach(([key, category]) => {
        console.log(`KnowledgeBase: Category ${key} has ${category.articles.length} articles`);
      });
      
      setCategories(newCategories);
    } catch (err) {
      console.error("Error in populateCategories:", err);
      setError("Error organizing knowledge base content. Please try again later.");
    }
  };

  const getFilteredContent = () => {
    try {
      // Make sure the active category exists in our categories map
      if (!categories[activeCategory]) {
        console.error("Active category doesn't exist:", activeCategory);
        return [];
      }
      
      let content = categories[activeCategory].articles;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        content = content.filter(item => {
          if (!item) return false;
          
          return (
            (item.title && item.title.toLowerCase().includes(query)) || 
            (item.summary && item.summary.toLowerCase().includes(query)) ||
            (item.content && Array.isArray(item.content) && item.content.some(section => 
              (section.title && section.title.toLowerCase().includes(query)) || 
              (section.content && section.content.toLowerCase().includes(query))
            ))
          );
        });
      }
      
      return content;
    } catch (err) {
      console.error("Error in getFilteredContent:", err);
      return [];
    }
  };

  const filteredContent = getFilteredContent();

  // Card rendering component to avoid repetition
  const renderArticleCards = (articles: SEOKnowledgeItem[]) => (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {articles.map((article) => (
        <motion.div
          key={article.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="h-full flex flex-col hover:shadow-md transition-shadow bg-white dark:bg-card border shadow">
            <CardContent className="p-6 flex flex-col flex-grow bg-white dark:bg-card">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-desert-sunset/10 text-desert-sunset mb-2">
                {article.categoryLabel}
              </span>
              <h3 className="text-lg font-bold mb-2 text-foreground">{article.title}</h3>
              <p className="text-muted-foreground text-sm mb-4 flex-grow">{article.summary}</p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link 
                  to={`/knowledge-base/${article.id}`} 
                  className="flex items-center justify-center"
                  title={`Read more about ${article.title}`}
                  aria-label={`Read the article: ${article.title}`}
                >
                  Read Article
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );

  return (
    <>
      <SEO seo={seoData} url={`https://losangelesseogroup.com${location.pathname}`} />
      <Navbar />
      <PageTransition>
        <main className="min-h-screen pt-24 pb-16">
          {error ? (
            <section className="section-padding">
              <div className="page-container">
                <Alert variant="destructive" className="mb-8">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="text-center">
                  <Button asChild>
                    <Link to="/">Return to home page</Link>
                  </Button>
                </div>
              </div>
            </section>
          ) : isLoading ? (
            <section className="section-padding">
              <div className="page-container">
                <div className="text-center py-12">
                  <h2 className="text-xl font-semibold mb-4">Loading knowledge base...</h2>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section className="section-padding bg-gradient-to-b from-desert-light to-background">
              <div className="page-container">
                <div className="max-w-3xl mx-auto text-center mb-12">
                  <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-cactus/10 text-desert-cactus">
                    Knowledge Base
                  </span>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    SEO <span className="text-gradient bg-cactus-gradient">Knowledge Base</span>
                  </h1>
                  <p className="text-lg text-foreground/70">
                    Everything you need to know about Search Engine Optimization - from basics to advanced strategies.
                  </p>
                </div>

                <div className="max-w-2xl mx-auto mb-12">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search the knowledge base..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  </div>
                </div>

                <Tabs 
                  defaultValue="all" 
                  className="max-w-4xl mx-auto"
                  onValueChange={setActiveCategory}
                >
                  <TabsList className="flex flex-wrap justify-center mb-8 h-auto p-1 bg-white/80 backdrop-blur-sm dark:bg-card/80">
                    {Object.entries(categories).map(([key, category]) => (
                      <TabsTrigger 
                        key={key} 
                        value={key} 
                        className="rounded-full px-4 py-2 m-1"
                        disabled={!category.articles || category.articles.length === 0}
                      >
                        {category.label} {category.articles && category.articles.length > 0 && `(${category.articles.length})`}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {Object.keys(categories).map(categoryKey => (
                    <TabsContent key={categoryKey} value={categoryKey} className="space-y-8">
                      {!categories[categoryKey].articles || categories[categoryKey].articles.length === 0 ? (
                        <div className="text-center py-12 bg-white dark:bg-card rounded-lg shadow-sm">
                          <h3 className="text-xl font-semibold mb-2">No articles found</h3>
                          <p className="text-muted-foreground">There are no articles in this category yet</p>
                        </div>
                      ) : filteredContent.length === 0 ? (
                        <div className="text-center py-12 bg-white dark:bg-card rounded-lg shadow-sm">
                          <h3 className="text-xl font-semibold mb-2">No results found</h3>
                          <p className="text-muted-foreground">Try adjusting your search query or category filter</p>
                        </div>
                      ) : renderArticleCards(filteredContent)}
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </section>
          )}
        </main>
      </PageTransition>
      <Footer />
    </>
  );
};

export default KnowledgeBase;
